import { GlButton, GlIcon, GlFormTextarea, GlFormGroup } from '@gitlab/ui';
import { translate } from '../../../../utils/i18n';
import __vue_normalize__ from 'vue-runtime-helpers/dist/normalize-component.js';

const PROCESSING_STATE = {
  APPROVING: 'approving',
  DENYING: 'denying',
  NONE: null
};
const i18n = {
  TOOL_APPROVAL_TITLE: translate('MessageToolApproval.toolApprovalTitle', 'Duo would like to execute a tool. Do you want to proceed?'),
  TOOL_APPROVAL_DESCRIPTION: translate('MessageToolApproval.toolApprovalDescription', 'GitLab Duo Agentic Chat wants to execute a tool. Do you want to proceed?'),
  TOOL_LABEL: translate('MessageToolApproval.toolLabel', 'Tool:'),
  TOOL_UNKNOWN: translate('MessageToolApproval.toolUnknown', 'Unknown'),
  APPROVE_TEXT: translate('MessageToolApproval.approveText', 'Approve'),
  APPROVING_TEXT: translate('MessageToolApproval.approvingText', 'Approving...'),
  DENY_TEXT: translate('MessageToolApproval.denyText', 'Deny'),
  DENYING_TEXT: translate('MessageToolApproval.denyingText', 'Denying...'),
  NO_PARAMETERS_TEXT: translate('MessageToolApproval.noParametersText', 'No parameters will be sent with this request.'),
  REQUEST_TEXT: translate('MessageToolApproval.parametersText', 'Request'),
  DENIAL_REASON_LABEL: translate('MessageToolApproval.denialReasonLabel', 'Rejection reason'),
  DENIAL_REASON_PLACEHOLDER: translate('MessageToolApproval.denialReasonPlaceholder', "Tell Duo why you're rejecting this tool execution..."),
  CANCEL_TEXT: translate('MessageToolApproval.cancelText', 'Cancel')
};
var script = {
  name: 'MessageToolApproval',
  components: {
    GlButton,
    GlIcon,
    GlFormTextarea,
    GlFormGroup
  },
  props: {
    message: {
      required: true,
      type: Object
    },
    isProcessing: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      showDenialReason: false,
      denialReason: '',
      localProcessingState: PROCESSING_STATE.NONE
    };
  },
  computed: {
    toolName() {
      var _this$message, _this$message$tool_in;
      return ((_this$message = this.message) === null || _this$message === void 0 ? void 0 : (_this$message$tool_in = _this$message.tool_info) === null || _this$message$tool_in === void 0 ? void 0 : _this$message$tool_in.name) || this.$options.i18n.TOOL_UNKNOWN;
    },
    toolParameters() {
      var _this$message2, _this$message2$tool_i;
      return ((_this$message2 = this.message) === null || _this$message2 === void 0 ? void 0 : (_this$message2$tool_i = _this$message2.tool_info) === null || _this$message2$tool_i === void 0 ? void 0 : _this$message2$tool_i.args) || {};
    },
    hasToolParameters() {
      return Object.keys(this.toolParameters).length > 0;
    },
    isApproving() {
      return this.isProcessing && this.localProcessingState === PROCESSING_STATE.APPROVING;
    },
    isDenying() {
      return this.isProcessing && this.localProcessingState === PROCESSING_STATE.DENYING;
    },
    buttonsDisabled() {
      return this.isProcessing;
    },
    approveButtonText() {
      return this.isApproving ? this.$options.i18n.APPROVING_TEXT : this.$options.i18n.APPROVE_TEXT;
    },
    denyButtonText() {
      return this.isDenying ? this.$options.i18n.DENYING_TEXT : this.$options.i18n.DENY_TEXT;
    }
  },
  watch: {
    // Reset local state when processing completes
    isProcessing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.resetProcessingState();
      }
    }
  },
  methods: {
    handleApprove() {
      if (this.isProcessing) return;
      this.localProcessingState = PROCESSING_STATE.APPROVING;
      this.$emit('approve-tool');
    },
    handleDeny() {
      if (this.isProcessing) return;
      this.showDenialReason = true;
    },
    cancelDenial() {
      this.showDenialReason = false;
      this.denialReason = '';
      this.localProcessingState = PROCESSING_STATE.NONE;
    },
    submitDenial() {
      if (this.isProcessing) return;
      this.localProcessingState = PROCESSING_STATE.DENYING;
      this.$emit('deny-tool', this.denialReason || null);
      // Don't reset state here - wait for isProcessing to change
    },
    resetProcessingState() {
      this.localProcessingState = PROCESSING_STATE.NONE;
      this.showDenialReason = false;
      this.denialReason = '';
    }
  },
  i18n
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"md gl-border gl-rounded-bl-none gl-border-1 gl-border-solid gl-border-transparent gl-bg-subtle gl-p-4 gl-leading-20 gl-text-default gl-break-anywhere"},[_c('p',{staticClass:"gl-mb-3 gl-text-gray-700"},[_vm._v("\n    "+_vm._s(_vm.$options.i18n.TOOL_APPROVAL_TITLE)+"\n  ")]),_vm._v(" "),_c('div',{staticClass:"gl-mb-3 gl-flex gl-items-center gl-gap-2"},[_c('gl-icon',{staticClass:"gl-text-gray-500",attrs:{"name":"work-item-maintenance"}}),_vm._v(" "),_c('strong',[_vm._v(_vm._s(_vm.toolName))])],1),_vm._v(" "),_c('div',{staticClass:"gl-border gl-mb-4 gl-rounded-base gl-border-gray-200 gl-bg-gray-50 gl-p-3"},[_c('p',{staticClass:"gl-mb-1 gl-text-sm gl-text-gray-500"},[_vm._v("\n      "+_vm._s(_vm.$options.i18n.REQUEST_TEXT)+"\n    ")]),_vm._v(" "),(_vm.hasToolParameters)?_c('code',{staticClass:"gl-whitespace-pre-wrap gl-text-sm gl-text-default gl-font-monospace",attrs:{"data-testid":"tool-parameters"}},[_vm._v(_vm._s(JSON.stringify(_vm.toolParameters, null, 2)))]):_c('span',{staticClass:"gl-text-sm gl-text-gray-500",attrs:{"data-testid":"no-parameters-message"}},[_vm._v("\n      "+_vm._s(_vm.$options.i18n.NO_PARAMETERS_TEXT)+"\n    ")])]),_vm._v(" "),(!_vm.showDenialReason)?_c('div',{staticClass:"gl-flex gl-justify-between"},[_c('gl-button',{attrs:{"variant":"danger","size":"small","icon":"cancel","data-testid":"deny-tool-inline","disabled":_vm.buttonsDisabled,"loading":_vm.isDenying},on:{"click":_vm.handleDeny}},[_vm._v("\n      "+_vm._s(_vm.denyButtonText)+"\n    ")]),_vm._v(" "),_c('gl-button',{attrs:{"variant":"confirm","size":"small","icon":"play","data-testid":"approve-tool-inline","disabled":_vm.buttonsDisabled,"loading":_vm.isApproving},on:{"click":_vm.handleApprove}},[_vm._v("\n      "+_vm._s(_vm.approveButtonText)+"\n    ")])],1):_c('div',{staticClass:"gl-mt-3"},[_c('gl-form-group',{staticClass:"gl-mb-3",attrs:{"label":_vm.$options.i18n.DENIAL_REASON_LABEL,"label-for":"inline-rejection-reason","optional":true}},[_c('gl-form-textarea',{attrs:{"id":"inline-rejection-reason","placeholder":_vm.$options.i18n.DENIAL_REASON_PLACEHOLDER,"rows":2,"no-resize":true,"submit-on-enter":false,"disabled":_vm.buttonsDisabled,"data-testid":"denial-reason-textarea","autofocus":""},on:{"submit":_vm.submitDenial},model:{value:(_vm.denialReason),callback:function ($$v) {_vm.denialReason=$$v;},expression:"denialReason"}})],1),_vm._v(" "),_c('div',{staticClass:"gl-flex gl-gap-3"},[_c('gl-button',{attrs:{"size":"small","data-testid":"cancel-denial","disabled":_vm.buttonsDisabled},on:{"click":_vm.cancelDenial}},[_vm._v("\n        "+_vm._s(_vm.$options.i18n.CANCEL_TEXT)+"\n      ")]),_vm._v(" "),_c('gl-button',{attrs:{"variant":"danger","size":"small","icon":"cancel","data-testid":"submit-denial","disabled":_vm.buttonsDisabled,"loading":_vm.isDenying},on:{"click":_vm.submitDenial}},[_vm._v("\n        "+_vm._s(_vm.denyButtonText)+"\n      ")])],1)],1)])};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = __vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export default __vue_component__;
export { PROCESSING_STATE, i18n };
